package com.havn.app.features.agency.domain

enum class HavnSubscriptionTier(val databaseValue: String) {
    Free("free"),
    SoloAgent("solo_agent"),
    ProAgent("pro_agent");

    companion object {
        fun fromDatabase(value: String?): HavnSubscriptionTier = when (value) {
            "solo_agent" -> SoloAgent
            "pro_agent" -> ProAgent
            else -> Free
        }
    }
}

enum class AgencyRole(val databaseValue: String) {
    Owner("owner"),
    Admin("admin"),
    Agent("agent"),
    Assistant("assistant"),
    Viewer("viewer");

    val displayName: String
        get() = when (this) {
            Owner -> "Owner"
            Admin -> "Admin"
            Agent -> "Agent"
            Assistant -> "Assistant"
            Viewer -> "Viewer"
        }

    val shortDescription: String
        get() = when (this) {
            Owner -> "Full agency control, including members, branding, assignments, listings, leads, and reports."
            Admin -> "Full agency management except deleting the agency."
            Agent -> "Works assigned listings, openings, leads, follow-ups, notes, and seller reports."
            Assistant -> "Supports assigned listings by editing leads, preparing follow-ups, and drafting reports."
            Viewer -> "Read-only access to dashboards and reports."
        }

    val canManageMembers: Boolean
        get() = this == Owner || this == Admin

    val canChangeRoles: Boolean
        get() = this == Owner || this == Admin

    val canDeleteAgency: Boolean
        get() = this == Owner

    val canManageBranding: Boolean
        get() = this == Owner || this == Admin

    val canManageAllProperties: Boolean
        get() = this == Owner || this == Admin

    val canDeleteProperties: Boolean
        get() = this == Owner || this == Admin

    val canAssignAgents: Boolean
        get() = this == Owner || this == Admin

    val canSeeAllLeads: Boolean
        get() = this == Owner || this == Admin

    val canEditAssignedLeads: Boolean
        get() = this == Owner || this == Admin || this == Agent || this == Assistant

    val canSendFollowUps: Boolean
        get() = this == Owner || this == Admin || this == Agent || this == Assistant

    val canCreateSellerReports: Boolean
        get() = this == Owner || this == Admin || this == Agent

    val canCreateDraftReports: Boolean
        get() = this == Assistant

    val isReadOnly: Boolean
        get() = this == Viewer

    fun capabilities(): List<String> = when (this) {
        Owner -> listOf(
            "Invite and remove members",
            "Change member roles",
            "Create, edit, and delete all properties",
            "Assign agents",
            "See all leads",
            "Export reports",
            "Change agency branding and settings",
            "Delete agency"
        )
        Admin -> listOf(
            "Invite and remove members",
            "Change member roles",
            "Create, edit, and delete all properties",
            "Assign agents",
            "See all leads",
            "Export reports",
            "Change agency branding and settings"
        )
        Agent -> listOf(
            "View assigned properties",
            "Host assigned openings",
            "See leads from assigned properties and openings",
            "Add notes",
            "Send follow-ups",
            "Create seller reports for assigned listings"
        )
        Assistant -> listOf(
            "View assigned properties",
            "Edit assigned leads",
            "Prepare follow-ups",
            "Create draft reports",
            "Cannot delete properties",
            "Cannot invite agents unless allowed"
        )
        Viewer -> listOf(
            "View dashboards",
            "View reports",
            "Cannot edit anything"
        )
    }

    companion object {
        fun fromDatabase(value: String?): AgencyRole = when (value) {
            "owner" -> Owner
            "admin" -> Admin
            "assistant" -> Assistant
            "viewer" -> Viewer
            else -> Agent
        }
    }
}

data class UserEntitlement(
    val tier: HavnSubscriptionTier = HavnSubscriptionTier.Free,
    val canUseAiFollowUps: Boolean = false,
    val canUseSellerReports: Boolean = false,
    val canUseAdvancedSellerReports: Boolean = false,
    val canUseAdvancedAnalytics: Boolean = false,
    val canCreateAgency: Boolean = false,
    val canJoinAgency: Boolean = false,
    val maxActivePersonalProperties: Int = 1,
    val expiresAt: String? = null
) {
    val isProAgent: Boolean
        get() = tier == HavnSubscriptionTier.ProAgent
}

data class Agency(
    val id: String,
    val name: String,
    val iconUrl: String? = null,
    val createdByAgentId: String,
    val workspaceId: String? = null,
    val role: AgencyRole = AgencyRole.Agent,
    val memberCount: Int = 1
)

enum class AgencyMemberStatus(val databaseValue: String) {
    Invited("invited"),
    Active("active"),
    Disabled("disabled");

    val displayName: String
        get() = when (this) {
            Invited -> "Invited"
            Active -> "Active"
            Disabled -> "Disabled"
        }

    companion object {
        fun fromDatabase(value: String?): AgencyMemberStatus = when (value) {
            "active" -> Active
            "disabled" -> Disabled
            else -> Invited
        }
    }
}

data class AgencyMember(
    val agentId: String?,
    val invitationId: String? = null,
    val fullName: String,
    val email: String,
    val avatarUrl: String? = null,
    val role: AgencyRole,
    val status: AgencyMemberStatus
)

data class AgencyInvitation(
    val id: String,
    val agencyId: String,
    val agencyName: String,
    val agencyIconUrl: String?,
    val role: AgencyRole,
    val invitedByName: String?,
    val createdAt: String
)

data class WorkspaceSelection(
    val id: String?,
    val agencyId: String?,
    val name: String,
    val isAgency: Boolean
)
